import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, SpinningLoader, Text, TextLink, useTooltip } from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';

type AudienceTrayFooterProps = PropsWithChildren & {
  hideBorder?: boolean;
};

export const AudienceTrayFooter = ({ children, hideBorder = false, ...css }: AudienceTrayFooterProps) => (
  <section
    css={{
      borderTop: hideBorder ? undefined : `1px solid ${theme.colors.neutral20}`,
      padding: theme.spacing(2, 4, 0, 4),
    }}
    {...css}
  >
    {children}
  </section>
);

const ActionBar = ({ children, ...css }: PropsWithChildren) => (
  <div
    css={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: theme.spacing(3),
    }}
    {...css}
  >
    {children}
  </div>
);

const AllotmentsLink = () => {
  const { t } = useTranslation('bulk-messaging');
  const { setMode, trackingIdPrefix } = useAudienceTray();

  return (
    <TextLink
      css={{
        display: 'flex',
        alignItems: 'center',
        columnGap: theme.spacing(0.75),
        minWidth: 'fit-content',
      }}
      onClick={() => setMode('allotments')}
      trackingId={`${trackingIdPrefix}-message-allotment-btn`}
    >
      <Icon name='locations' />
      <Text as='span' weight='bold' color='primary'>
        {t('Message Allotment')}
      </Text>
    </TextLink>
  );
};

type RecipientCountProps = {
  count?: number;
  description?: string;
  isDisabled?: boolean;
  isError?: boolean;
  tooltip?: string;
};

const RecipientCount = ({ count, description, isDisabled = false, isError = false, tooltip }: RecipientCountProps) => {
  const { t } = useTranslation('bulk-messaging');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top-end' });
  const isLoading = count === undefined && !isError;

  const text = useMemo(() => {
    if (isError) return t('Cannot Count Contacts');
    if (count === undefined) return t('Counting Contacts...');
    return t('{{count}} Contact', { count });
  }, [t, isError, count]);

  return (
    <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Text
        color={isError ? 'error' : isDisabled ? 'disabled' : 'subdued'}
        css={{ display: 'flex', alignItems: 'center', fontSize: theme.fontSize(24) }}
        weight='bold'
      >
        {isLoading && <SpinningLoader size='xs' css={{ marginRight: theme.spacing(0.5) }} />}
        {text}
      </Text>
      {description && !isLoading && (
        <Text
          color={isError ? 'error' : isDisabled ? 'disabled' : 'subdued'}
          css={{ display: 'flex', alignItems: 'center' }}
        >
          {description}
          {tooltip && (
            <NakedButton {...triggerProps}>
              <Icon name='info-small' css={{ marginLeft: theme.spacing(0.5) }} />
            </NakedButton>
          )}
        </Text>
      )}
      <Tooltip {...tooltipProps}>{tooltip}</Tooltip>
    </div>
  );
};

AudienceTrayFooter.ActionsBar = ActionBar;
AudienceTrayFooter.AllotmentsLink = AllotmentsLink;
AudienceTrayFooter.RecipientCount = RecipientCount;
